import { isPlatformBrowser } from '@angular/common';
import {
 DestroyRef,
 Injectable,
 PLATFORM_ID,
 TemplateRef,
 WritableSignal,
 inject,
 makeStateKey,
 signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TransferState } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GetAllReviewsPayloadModel,
 GetAllVotesEcolodgePayloadModel,
 GetAllVotesPayloadModel,
} from '@core/models/domains/comments';
import { AuthService } from '@core/services/authentication/auth.service';
import { MomentService } from '@core/services/moment.service';
// import { OpentelemetryService } from '@core/services/opentelemetry.service';
import { HotelSummaryModel, RoomDetailsPayload } from '@models/hotel';
import { normalizeHotelSummary } from '@models/hotel-summary.mapper';
import { ComboRooms, RoomInfo, RoomModel, RoomsApiModel } from '@models/room';
import { PDPRepository } from '@repos/pdp.repository';
import { AutocompleteService } from '@shared/services/autocomplete.service';
import { DatepickerService } from '@shared/services/datepicker.service';
import { SearchBoxService } from '@shared/services/serach-box.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

@Injectable()
export class HotelInfoUiService {
 hotelinfoRepo = inject(PDPRepository);
 searchBoxService = inject(SearchBoxService);
 datePickerService = inject(DatepickerService);
//  opentelemetryService = inject(OpentelemetryService);
 autocompleteService = inject(AutocompleteService);
 authService = inject(AuthService);
 momentService = inject(MomentService);
 platformid = inject(PLATFORM_ID);

 transferState = inject(TransferState);

 constructor() {}

 route = inject(ActivatedRoute);
 router = inject(Router);
 destroyRef = inject(DestroyRef);

 hotelFullName = '';
 hotelId = 0;
 hosueId = 0;
 nights = 0;
 startDate = '';
 endDate = '';
 ssrInit: WritableSignal<boolean> = signal(false);
 ecolodgTotalPrice: WritableSignal<any> = signal(0);
 lowestRoomPrice = 0;
 hotelInfo: WritableSignal<HotelSummaryModel> = signal({} as HotelSummaryModel);
 activeRoomFiltersIds: WritableSignal<Array<number>> = signal([]);
 activeRoomFiltersNames: WritableSignal<Array<string>> = signal([]);
 selectedRooms: WritableSignal<Array<RoomInfo & { count: number; id: number; name: string }>> =
  signal([]);
 ecolodgeRoomInfo: WritableSignal<Array<RoomInfo & { count: number; id: number; name: string }>> =
  signal([]);
 resetCountAll: WritableSignal<boolean | number> = signal(false); // this number could be -1(for remove all) or a roomId
 ExtraGuestCount: WritableSignal<number> = signal(0);
 FixedGuestCount: WritableSignal<number> = signal(0);
 resetCountSpecific: WritableSignal<boolean | number> = signal(false); // this number could be a roomId
 allRooms: WritableSignal<Array<RoomInfo[]>> = signal([]);
 comboRooms: WritableSignal<Array<ComboRooms>> = signal([]);
 selectedComboRooms: WritableSignal<Array<ComboRooms>> = signal([]);
 roomsLoading: WritableSignal<boolean> = signal(false);
 signalRoomApiResponse: WritableSignal<boolean> = signal(false);
 mobileSearchBarOpen: WritableSignal<boolean> = signal(false);
  roomApiResponse$ = new BehaviorSubject<RoomsApiModel>({} as RoomsApiModel);
  similarHotels$ = new BehaviorSubject<any>({});

 getHotelInfoSummary(
  hotelPath: string,
  date?: string,
  endDate?: string,
  accommodation = 'hotel'
 ): void | Observable<HotelSummaryModel> {
  const searchDates = this.resolveSearchDates(date, endDate);
  date = searchDates.startDate;
  endDate = searchDates.endDate;
  this.updateSearchDates(date, endDate);

  const summaryKey = makeStateKey<HotelSummaryModel>('summary');
  const ssrInit = makeStateKey<HotelSummaryModel>('ssrInit');
  const summaryUrl = this.hotelinfoRepo.getHotelInfoSummaryUrl(
   hotelPath,
   date,
   endDate,
   accommodation
  );

  const summary$ = this.hotelinfoRepo
   .getHotelInfoSummary(hotelPath, date, endDate, accommodation)
   .pipe(
    map((response) => normalizeHotelSummary(response)),
    tap((response: HotelSummaryModel) => {
     this.hotelInfo.set(response);
     this.hotelId = response.HotelId;
     this.hotelFullName = response.Name;
     this.setSearchDataOnInit(
      response.Name,
      searchDates.startDate,
      searchDates.endDate
     );
     // Gallery modal reads ModalHotelGallery.Pictures (including categories), so the
     // transferred summary must keep the full list.
     this.transferState.set(summaryKey, response);
     this.dropTransferredHttpResponse(summaryUrl);
    })
   );

  if (!isPlatformBrowser(this.platformid)) {
   this.transferState.set(ssrInit, true);
   this.roomsLoading.set(true);
   return summary$;
  } else {
   const routeParamRefer = this.route.snapshot.queryParams['refer'];

   if (
    (routeParamRefer == 'home' ||
     !routeParamRefer ||
     routeParamRefer == 'searchresult' ||
     routeParamRefer == 'hotelinfo' ||
     routeParamRefer == 'iranlist') &&
    !this.transferState.get(ssrInit)
   ) {
    this.transferState.set(summaryKey, undefined);
   }
   const cachedResponse = this.transferState.get(summaryKey);
   if (cachedResponse) {
    const response = normalizeHotelSummary(cachedResponse);
    this.hotelInfo.set(response);
    this.hotelId = response.HotelId;
    this.hotelFullName = response.Name;

    this.setSearchDataOnInit(
     response.Name,
     response.SearchSection.StartDate,
     response.SearchSection.EndDate
    );
    this.transferState.set(ssrInit, false);
   } else summary$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
 }

 /**
  * `TransferHttpCacheModule` serializes every SSR GET response into the page. The summary is
  * already transferred under `summary` in its normalized shape, so the raw copy is never read
  * on the client and only inflates the HTML.
  */
 private dropTransferredHttpResponse(url: string): void {
  if (isPlatformBrowser(this.platformid)) return;

  // `provideClientHydration` uses Angular's hashed `GET.json.<url>?` key, while the deprecated
  // `TransferHttpCacheModule` uses the readable `G.json.<url>?` key. The normalized `summary`
  // state below is the client hydration source, so neither raw response should be serialized too.
  const hydrationCacheKey = this.hashTransferCacheKey(`GET.json.${url}?`);
  this.transferState.remove(makeStateKey(hydrationCacheKey));
  this.transferState.remove(makeStateKey(`G.json.${url}?`));
 }

 /** Matches Angular 16's internal HTTP transfer-cache key hashing. */
 private hashTransferCacheKey(value: string): string {
  let hash = 0;

  for (const char of value) {
   hash = (Math.imul(31, hash) + char.charCodeAt(0)) << 0;
  }

  return (hash + 2147483648).toString();
 }

 /** Use the same complete date range for related PDP requests. */
 private resolveJalaliDates(): [string, string] {
  const { startDate, endDate } = this.syncDatesFromQueryParams();
  this.updateSearchDates(startDate, endDate);
  return [startDate, endDate];
 }

 calculateLowestRoomPrice(rooms: any) {
  let lowestPrice = rooms.room.roomContainer[0]?.roomInfos[0]?.roomPrice?.price?.ihoPrice;
  rooms.room.roomContainer.forEach((room: any) => {
   if (
    room.roomInfos[0].roomPrice?.price?.ihoPrice < lowestPrice &&
    room.roomInfos[0].roomPrice?.price?.ihoPrice != 0
   )
    lowestPrice = room?.roomInfos[0]?.roomPrice?.price?.ihoPrice;
  });
  this.lowestRoomPrice = lowestPrice;
 }

 private resolveSearchDates(startDate?: string, endDate?: string): { startDate: string; endDate: string } {
  if (!startDate?.trim() || !endDate?.trim()) {
   return this.momentService.getTodayAndTomorrowJalali();
  }

  return {
   startDate: this.datePickerService.normalizeDate(startDate.trim()),
   endDate: this.datePickerService.normalizeDate(endDate.trim()),
  };
 }

 /** Use a complete URL date range, or today and tomorrow when either date is missing. */
 syncDatesFromQueryParams(): { startDate: string; endDate: string } {
  const queryParams = this.route.snapshot.queryParams;
  return this.resolveSearchDates(queryParams?.['date'], queryParams?.['endDate']);
 }

 applySearchDates(startDate: string, endDate: string): void {
  const dates = isPlatformBrowser(this.platformid)
   ? this.syncDatesFromQueryParams()
   : this.resolveSearchDates(startDate, endDate);
  this.updateSearchDates(dates.startDate, dates.endDate);
 }

 private updateSearchDates(startDate: string, endDate: string): void {
  this.searchBoxService.dateRange = [startDate, endDate];
  this.datePickerService.setStartDate(startDate);
  this.datePickerService.setEndDate(endDate);
  this.startDate = startDate;
  this.endDate = endDate;
 }

 setSearchDataOnInit(name: string, startDate: string, endDate: string) {
  this.searchBoxService.residentId = this.hotelId;
  this.searchBoxService.residentType = 3;
  this.searchBoxService.residentName = name;
  this.applySearchDates(startDate, endDate);
  this.autocompleteService.setSelectedItem({
   id: this.hotelId,
   type: 3,
   name,
  });
 }

 getRooms() {
  const [startDate, checkoutDate] = this.searchBoxService.getStartAndEndDate();
  this.applySearchDates(startDate, checkoutDate);
  const [date, endDate] = this.searchBoxService.getStartAndEndDate();
  this.roomsLoading.update(() => true);
  return this.hotelinfoRepo
   .getRooms(this.hotelId, date, endDate)
   .pipe(takeUntilDestroyed(this.destroyRef))
   .subscribe((res) => {
    this.calculateLowestRoomPrice(res);
    this.startDate = date
    this.endDate = endDate
    this.roomApiResponse$.next(res);
    this.signalRoomApiResponse.set(true);
    this.selectedRooms.update(() => []);


    // if(res.room.combenations?.length) {
    this.selectedComboRooms.update(() => []);
    this.comboRooms.update(() => {
     res.room?.combenations?.[0]?.rooms?.forEach((each) => {
      each.priceInfo.checkInDate = this.momentService.convertISOtoJalali(
       each.priceInfo.checkInDate
      );
      each.priceInfo.checkOutDate = this.momentService.convertISOtoJalali(
       each.priceInfo.checkOutDate
      );
     });

     return res.room?.combenations;
    });
    // }

    this.nights =
     res.room.roomContainer?.[0]?.roomInfos[0]?.roomPrice?.price?.nights ??
     (this.datePickerService._selectedNights().length - 1 || this.hotelInfo().SearchSection.Night);
    if (res.room.roomContainer.length)
     this.allRooms.set(
      res.room.roomContainer.map((eachRoom: RoomModel) => [...eachRoom.roomInfos])
     );
    else this.allRooms.set([]);

    this.roomsLoading.set(false);
   });
 }

 getSimilarHotels(hotelId: number, hotelPrice: number) {
  const [date, endDate] = this.resolveJalaliDates();

   return this.hotelinfoRepo.getSimilarHotels(hotelId, date, endDate, hotelPrice).pipe(takeUntilDestroyed(this.destroyRef))
   .subscribe((res) => {
     this.similarHotels$.next(res);
    return res;
   });
 }

 getOtherRooms(parentId: number, type: number) {
  const [date, endDate] = this.resolveJalaliDates();
  return this.hotelinfoRepo.getOtherRooms(parentId, date, endDate, type);
 }
 getSimilarAccommodation(hotelId: number, houseId: string, type: number) {
  const [date, endDate] = this.resolveJalaliDates();
  return this.hotelinfoRepo.getSimilarAccommodation(hotelId, houseId, date, endDate, type);
 }

 getVoteSection(hid: number) {
  return this.hotelinfoRepo.getVoteSection(hid);
 }
 getVoteAll(model: GetAllVotesPayloadModel) {
  return this.hotelinfoRepo.getVotes(model);
 }
  getVoteReviews(model: GetAllReviewsPayloadModel) {
    return this.hotelinfoRepo.getReviews(model);
  }
 getVoteAllEcolodge(model: GetAllVotesEcolodgePayloadModel) {
  return this.hotelinfoRepo.getVotesEcolodge(model);
 }
 getVoteFilters(hid: number) {
  return this.hotelinfoRepo.getVoteFilters(hid);
 }
 getReviewsHotels(peyload:any, isEcolodge = false) {
  return this.hotelinfoRepo.getReviewsHotels(peyload, isEcolodge);
 }
 getReviewsCount() {
  return this.hotelinfoRepo.getReviewsCount();
  }
  getLatestReviews() {
    return this.hotelinfoRepo.getLatestReviews();
  }
  getTopRateHotels() {
    return this.hotelinfoRepo.getTopRateHotels();
  }
  getMarkedCities() {
    return this.hotelinfoRepo.getMarkedCities();
  }
  getStateCities() {
    return this.hotelinfoRepo.getStateCities();
  }
  getRveiewsOfCity(cityName: string) {
    return this.hotelinfoRepo.getRveiewsOfCity(cityName);
  }
 

 getGalleryHotel(hid: number) {
  return this.hotelinfoRepo.getGalleryInfo(hid);
 }
 getRoomDetails(model: RoomDetailsPayload) {
  return this.hotelinfoRepo.getRoomDetails(model);
 }

 // method to increase the number of selected rooms by one
 increaseSelectedRooms(newRoom: RoomInfo, roomId: number, roomName: string, isCombo: boolean) {
  const rooms = this.selectedRooms();
  const foundItem = rooms.find((eachRoom) => eachRoom.id === roomId);
  const otherRooms = rooms.filter((each) => each.id !== roomId);
  if (foundItem) {
   this.selectedRooms.update(() => [
    ...otherRooms,
    { ...newRoom, count: foundItem.count + 1, id: roomId, name: roomName },
   ]);
  } else {
   this.selectedRooms.update(() => [
    ...otherRooms,
    { ...newRoom, count: 1, id: roomId, name: roomName },
   ]);
  }
  // this.opentelemetryService.startSpan('Room selection');
  // this.opentelemetryService.sendEvent('Room selection', {
  //  Session_Id: '',
  //  'mobile phone': this.authService._userData()?.mobile,
  //  First_track_id: '',
  //  Destination: this.autocompleteService._selectedItem()?.name,
  //  startDate: this.datePickerService._startDate(),
  //  endDate: this.datePickerService._endDate(),
  // });
  // this.opentelemetryService.endSpan();
 }

 // method to increase the number of selected rooms by one
 decreaseSelectedRooms(roomId: number, roomName: string, isCombo: boolean) {
  const rooms = this.selectedRooms();
  const foundItem = rooms.find((eachRoom) => eachRoom.id === roomId);

  if (!rooms.length || !foundItem) return;

  if (foundItem.count > 1) {
   const newRooms = rooms.filter((each) => each.id !== foundItem.id);
   this.selectedRooms.update(() => [
    ...newRooms,
    { ...foundItem, count: foundItem.count - 1, name: roomName },
   ]);
  } else this.selectedRooms.update(() => [...rooms.filter((each) => each.id !== foundItem.id)]);
  // this.opentelemetryService.startSpan('Room selection');
  // this.opentelemetryService.sendEvent('Room selection', {
  //  Session_Id: '',
  //  'mobile phone': this.authService._userData()?.mobile,
  //  First_track_id: '',
  //  Destination: this.autocompleteService._selectedItem()?.name,
  //  startDate: this.datePickerService._startDate(),
  //  endDate: this.datePickerService._endDate(),
  // });
  // this.opentelemetryService.endSpan();
 }

 addComboRoomsToSelection(comboRoomsId?: string) {
  const comboRooms = this.comboRooms().find((room) => room.combenationId === comboRoomsId);
  if (comboRooms && comboRoomsId) this.selectedComboRooms.update(() => [comboRooms]);
 }

 removeComboRoomsSelection(comboRoomsId?: string) {
  this.selectedComboRooms.update(() => []);
 }

 addToFavorite() {
  return this.hotelinfoRepo.addToFavorite(this.hotelId);
 }

 setPackage(startDate: string, endDate: string) {
  this.datePickerService.setStartDate(startDate);
  this.datePickerService.setEndDate(endDate);
  this.router
   .navigate([], {
    queryParams: {
     date: startDate,
     endDate: endDate,
    },
    queryParamsHandling: 'merge',
    relativeTo: this.route,
   })
   .finally(() => this.getRooms());
 }

 getReserveUUID(reserveData: Record<string, any>) {
  return this.hotelinfoRepo.getReserveUUID(reserveData);
 }

 getEcolodgeCalenderDetails(
  range: { StartDate: string; EndDate: string },
  accommodationData: { hotelId: string; houseId: string; type: string },
  isFirstRequest: boolean = false
 ) {
  return this.hotelinfoRepo.getCalenderDetails(range, accommodationData, isFirstRequest);
 }

 setMobileSearchBarOpen(value: boolean) {
  this.mobileSearchBarOpen.set(value);
  this.searchBoxService.mobileSearchBoxIsOpen.next(value);
 }
}
